import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { Capacitor } from "@capacitor/core";
import { createVippsPayment } from "../payments/vipps";
import { paymentReturnUrl } from "../payments/appLinks";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../supabase";
import { fetchOwnProfile } from "../../utils/userProfile";
import { toKlippekortCardShape } from "../../utils/ticketMapping";
import { COUNTRY_CODES } from "../../utils/countryCodes";
import { FiUser, FiTag, FiCheckCircle, FiXCircle, FiShoppingCart, FiChevronDown, FiArrowLeft, FiCreditCard } from "react-icons/fi";
import { BannerSkeleton, PointsSkeleton, FormSkeleton } from "../../components/ui/SkeletonCard";
import { calculateRawTotal, calculateDiscountAmount, calculateTotalPrice, validateDiscount, discountRowToValidationShape, isActiveKlippekort, wouldExceedKlippekortCap, exceedsOrderCap, pointsForPurchase } from "./cartPricing";
import "../../components/common/SystemState.css";
import "./MyCart.css";
import { useParkSettings, isPointsCampaign } from "../../utils/parkSettings";

const CART_TTL_MS = 5 * 60 * 1000;

// crypto.randomUUID() only works in secure contexts (HTTPS or localhost) -
// testing over plain HTTP on a LAN IP (e.g. http://10.0.0.8:3000, needed to
// open the app on a real phone for a real Vipps redirect) throws instead.
// crypto.getRandomValues() has no such restriction, so build a real UUID v4
// from it by hand - keeps the "must not be guessable" property the payment
// reference needs (see generatePaymentReference's caller) even then.
function generatePaymentReference() {
  if (typeof crypto?.randomUUID === 'function') return crypto.randomUUID();
  if (typeof crypto?.getRandomValues === 'function') {
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
    bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant
    const hex = [...bytes].map(b => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }
  // Last-resort fallback for an environment with neither API at all.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    return (c === 'x' ? r : ((r & 0x3) | 0x8)).toString(16);
  });
}

// Same shared users/{email} doc cache Home/Profile/Settings already write, so the
// buyer-info form doesn't have to show its skeleton every single time you open the cart.
const getCachedUserDoc = () => {
  try { return JSON.parse(localStorage.getItem('pws_user_cache')); } catch { return null; }
};

export default function MyCart() {
  const { t, i18n: i18nInstance } = useTranslation('mycart');
  const { pointsPercent } = useParkSettings();
  const cachedUser = getCachedUserDoc();
  const canUseCache = Boolean(cachedUser && localStorage.getItem('pws_auth') === '1');
  const [cartItems, setCartItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem("cartItems")) || []; } catch { return []; }
  });
  const [phoneNumber, setPhoneNumber] = useState(canUseCache ? (cachedUser.phone || "").replace(/^47/, "") : "");
  // Only used for CARD - WALLET is always Norway, so its +47 stays fixed and
  // never needs this. Defaults to Norway too, since most card customers are
  // still local - a tourist just picks their own from the native picker.
  const [countryCode, setCountryCode] = useState('47');
  const [buyerName, setBuyerName] = useState(canUseCache ? (cachedUser.name || "") : "");
  const [email, setEmail] = useState(canUseCache ? (cachedUser.email || "") : "");
  const [loading, setLoading] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(canUseCache ? true : null);
  // Freestanding card payments (mainly for tourists/international customers
  // without the Vipps app) reuse the exact same create/capture/refund flow -
  // just a different paymentMethod.type sent to Vipps.
  const [paymentMethod, setPaymentMethod] = useState('WALLET');
  // Guest checkout: a one-time buy with no account, no separate opt-in step
  // anymore - logging in is already offered on the chip, so anyone not
  // logged in just checks out as a guest directly. Only simple golf/lek
  // tickets - klippekort/poeng/kuponger are all account-bound and stay
  // members-only (still enforced at pay-time in handlePayNow).
  const guestMode = isLoggedIn === false;
  const [memberName, setMemberName] = useState(canUseCache ? (cachedUser.name || "") : "");
  const [memberId, setMemberId] = useState(canUseCache ? (cachedUser.memberId || "") : "");
  // Members' contact fields are pre-filled from their account, so they're
  // collapsed by default behind the chip - guests have nothing pre-filled and
  // always see the fields expanded.
  const [contactExpanded, setContactExpanded] = useState(false);

  // Rabattkode
  const [discountFieldOpen, setDiscountFieldOpen] = useState(false);
  const [discountCode, setDiscountCode] = useState('');
  const [discountInput, setDiscountInput] = useState('');
  const [discountData, setDiscountData] = useState(null); // { id, discountType, discountValue, description }
  const [discountStatus, setDiscountStatus] = useState(null); // 'valid' | 'invalid' | 'checking'
  const [discountError, setDiscountError] = useState('');

  const navigate = useNavigate();

  // window.location.href = vippsResponse.url (in handlePayNow) navigates away
  // with loading=true still set. If the browser restores this page from
  // back/forward cache afterward (Vipps's own cancel button, or the browser's
  // back button) instead of a fresh reload, React's state comes back frozen
  // exactly as it was - the pay button stays stuck in its loading spinner
  // forever, since handlePayNow's own `if (loading) return` blocks retrying.
  // pageshow's persisted flag is the standard way to detect a bfcache restore.
  useEffect(() => {
    const handlePageShow = (event) => {
      if (event.persisted) setLoading(false);
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  useEffect(() => {
    const cartTimestamp = Number(localStorage.getItem("cartTimestamp")) || 0;
    const isExpired = cartTimestamp > 0 && Date.now() - cartTimestamp > CART_TTL_MS;
    if (isExpired) {
      setCartItems([]);
      localStorage.removeItem("cartItems");
      localStorage.removeItem("cartTimestamp");
      localStorage.removeItem("productQuantities");
    }
    // Autofill fields if user is logged in
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      const user = session?.user;
      setIsLoggedIn(!!user);
      if (user) {
        setEmail(user.email || "");
        // Optionally fetch name/phone from the user's own profile row:
        try {
          const data = await fetchOwnProfile(user.id);
          if (data) {
            setBuyerName(data.name || "");
            setMemberName(data.name || "");
            setMemberId(data.memberId || "");
            setPhoneNumber((data.phone || "").replace(/^47/, ""));
            try {
              const existing = getCachedUserDoc() || {};
              localStorage.setItem('pws_user_cache', JSON.stringify({ ...existing, ...data }));
            } catch {}
          }
        } catch (err) {
          // ignore
        }
      } else {
        setIsLoggedIn(false);
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  const rawTotal = calculateRawTotal(cartItems);
  const discountAmount = calculateDiscountAmount(rawTotal, discountData);
  const totalPrice = calculateTotalPrice(rawTotal, discountAmount);

  const applyDiscountCode = async () => {
    const code = discountInput.trim().toUpperCase();
    if (!code) return;
    setDiscountStatus('checking');
    setDiscountError('');
    setDiscountData(null);
    setDiscountCode('');
    try {
      const { data: row, error } = await supabase.from('discount_codes').select('*').eq('code', code).maybeSingle();
      if (error) throw error;
      if (!row) {
        setDiscountStatus('invalid');
        setDiscountError('Koden finnes ikke.');
        return;
      }
      const d = discountRowToValidationShape(row);
      const result = validateDiscount(d, rawTotal);
      if (!result.valid) {
        setDiscountStatus('invalid');
        setDiscountError(result.error);
        return;
      }
      setDiscountData(d);
      setDiscountCode(code);
      setDiscountStatus('valid');
    } catch (err) {
      setDiscountStatus('invalid');
      setDiscountError('Kunne ikke sjekke koden. Prøv igjen.');
    }
  };

  const removeDiscount = () => {
    setDiscountData(null);
    setDiscountCode('');
    setDiscountInput('');
    setDiscountStatus(null);
    setDiscountError('');
  };

  // WALLET must be a real Norwegian MSISDN - it has to resolve to an actual
  // registered Vipps account. CARD has no such requirement (per Vipps's own
  // ePayment API docs, customer.phoneNumber there is optional, MSISDN-format
  // contact metadata, not tied to a Vipps account) - it's the path meant for
  // tourists/international customers, so accept any reasonable full MSISDN
  // length (7-15 digits, the international range) once countryCode is glued on.
  const isValidPhone = (phone, method) =>
    method === 'CARD' ? /^\d{7,15}$/.test(phone) : /^47\d{8}$/.test(phone);

  const handlePhoneChange = (e) => {
    let val = e.target.value.replace(/\D/g, "");
    // CARD's local part has its own country code as a separate picker now, so
    // it just needs enough room for any country's national number (up to ~12
    // digits) - WALLET stays fixed at Norway's exact 8.
    val = val.slice(0, paymentMethod === 'CARD' ? 12 : 8);
    setPhoneNumber(val);
  };

  const MAX_ACTIVE_KLIPPEKORT = 3;
  const MAX_ORDER_TOTAL = 5000; // fat-finger guard, not a business ceiling

  // Counts the customer's currently active (unused-up) klippekort across every order.
  const countActiveKlippekort = async (userEmail) => {
    const { data: userRow } = await supabase.from('users').select('id').eq('email', userEmail.toLowerCase()).maybeSingle();
    if (!userRow) return 0;
    const { data: kkRows, error } = await supabase.from('klippekort').select('*').eq('user_id', userRow.id);
    if (error) throw error;
    return (kkRows || []).filter(row => isActiveKlippekort(toKlippekortCardShape(row))).length;
  };

  const handlePayNow = async (e) => {
    e.preventDefault();
    if (loading) return;

    // CARD glues the picked country code onto the local number - WALLET is
    // always "47" since it's Norway-only, no picker shown for it at all.
    const fullPhoneNumber = paymentMethod === 'CARD' ? countryCode + phoneNumber : "47" + phoneNumber;

    // Guests paying with Vipps only enter a phone number - Vipps's own
    // profile-sharing consent supplies name/email afterward. That consent
    // isn't supported for card payments at all, so guest+card still needs
    // the manual fields. Members already have both on file from their account.
    if (!guestMode || paymentMethod === 'CARD') {
      if (!buyerName.trim()) {
        toast.error(t('errorEnterName'));
        return;
      }
      if (!email.trim()) {
        toast.error(t('errorEnterEmail'));
        return;
      }
    }
    if (!phoneNumber.trim()) {
      toast.error(t('errorEnterPhone'));
      return;
    }
    if (!isValidPhone(fullPhoneNumber, paymentMethod)) {
      toast.error(paymentMethod === 'CARD' ? t('errorInvalidPhoneIntl') : t('errorInvalidPhone'));
      return;
    }

    if (exceedsOrderCap(totalPrice, MAX_ORDER_TOTAL)) {
      toast.error(t('errorMaxOrderTotal', { max: MAX_ORDER_TOTAL }));
      return;
    }

    if (guestMode) {
      // Klippekort/poeng/kuponger are account-bound - a guest checkout can only
      // ever be simple golf/lek tickets, so there's no active-count cap to check.
      if (cartItems.some(item => item.category === 'klippekort' || item.category === 'månedskort')) {
        toast.error(t('errorKlippekortNeedsAccount'));
        return;
      }
    } else {
      const newKlippekortCount = cartItems.filter(item => item.category === 'klippekort').length;
      if (newKlippekortCount > 0) {
        try {
          const activeCount = await countActiveKlippekort(email.trim());
          if (wouldExceedKlippekortCap(activeCount, newKlippekortCount, MAX_ACTIVE_KLIPPEKORT)) {
            toast.error(t('errorMaxKlippekort', { max: MAX_ACTIVE_KLIPPEKORT }));
            return;
          }
        } catch (err) {
          console.error("Klippekort cap check failed:", err);
          // Don't block a real purchase over a failed read - just skip the cap this once.
        }
      }
    }

    setLoading(true);

    // Generate the payment reference once here. Must not be guessable -
    // /payment-details, /capture-payment and /refund-payment all key off this
    // value, so a predictable reference (e.g. a timestamp) would let someone
    // brute-force their way to other customers' payments. Vipps' own docs
    // recommend a UUID-style reference and explicitly warn against numeric IDs.
    const reference = generatePaymentReference();

    const paymentData = {
      // Sent only so the backend can warn on a mismatch - the backend always
      // recomputes the real charge from Firestore product prices + a
      // re-validated discount code rather than trusting this value.
      amountValue: Math.round(totalPrice * 100),
      phoneNumber: fullPhoneNumber,
      buyerName: buyerName.trim(),
      email: email.trim(),
      reference: reference,
      returnUrl: paymentReturnUrl(Capacitor.getPlatform(), window.location.origin),
      paymentDescription: `Ordre OR-${reference.substring(0, 8)}`,
      cartItems: cartItems.map(item => ({ productId: item.productId, quantity: item.quantity })),
      discountCode: discountCode || null,
      discountAmount: discountAmount || 0,
      // Ask Vipps's consent screen for name/email since guests didn't type
      // them - members already have real profile data, so never requested.
      requestProfile: guestMode,
      paymentMethod,
    };

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const vippsResponse = await createVippsPayment(paymentData, guestMode ? null : session?.access_token);
      if (vippsResponse?.url) {
        // Store the reference and other info locally to track this payment/order
        localStorage.setItem("orderReference", reference);
        // Tickets.jsx's orphaned-payment recovery check needs this to tell "PaymentReturn
        // is still mid-flight checking Vipps" apart from "genuinely abandoned" - without
        // it, cancelling and quickly navigating elsewhere gets hijacked back into the
        // cancelled screen while that check is still in flight.
        localStorage.setItem("orderReferenceSetAt", String(Date.now()));
        localStorage.setItem("phoneNumber", fullPhoneNumber);
        localStorage.setItem("buyerName", buyerName.trim());
        localStorage.setItem("email", email.trim());
        localStorage.setItem("paymentMethod", paymentMethod);
        if (discountCode) localStorage.setItem("discountCode", discountCode);
        if (discountData?.id) localStorage.setItem("discountId", discountData.id);
        if (discountAmount > 0) localStorage.setItem("discountAmount", String(discountAmount));
        if (guestMode) {
          localStorage.setItem("isGuestCheckout", "1");
        } else {
          localStorage.removeItem("isGuestCheckout");
        }

        // Vipps blocks being iframed (frame-ancestors), so redirect the whole page.
        window.location.href = vippsResponse.url;
        return;
      } else {
        setLoading(false);
        console.error('❌ No URL in Vipps response:', vippsResponse);
        toast.error(t('errorNoVippsUrl'));
      }
    } catch (error) {
      setLoading(false);
      console.error("❌ Full payment error:", error);
      console.error("Error message:", error?.message);
      console.error("Error stack:", error?.stack);
      
      let errorMsg = error?.message || "Unknown error";
      
      // Try to parse JSON error
      try {
        const parsed = JSON.parse(errorMsg);
        errorMsg = parsed.error || parsed.details || JSON.stringify(parsed);
      } catch (e) {
        // Not JSON, use as-is
      }
      
      toast.error(t('errorPaymentFailed', { message: errorMsg }));
    }
  };

  // Show once auth state has resolved, so this doesn't flash before we know
  // whether to also skip straight past the login-gated checkout below.
  if (isLoggedIn !== null && cartItems.length === 0) {
    return (
      <div className="system-state">
        <div className="system-state-icon-badge system-state-icon-badge--yellow">
          <FiShoppingCart size={24} />
        </div>
        <h2 className="system-state-title">{t('emptyCartTitle')}</h2>
        <p className="system-state-desc">{t('emptyCartDesc')}</p>
        <div className="system-state-actions">
          <button className="system-state-btn-primary" onClick={() => navigate('/Products')}>
            {t('goToProducts')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mycart-page">

      {/* ── Kontaktinformasjon label - always visible above the chip; the
          fields themselves collapse behind the chip for members ── */}
      <div className="section-label section-label--no-top">{t('kontaktinformasjon')}</div>

      {/* ── User chip ── */}
      {isLoggedIn === null && <BannerSkeleton />}
      {isLoggedIn === false && (
        <div className="user-chip" style={{ cursor: 'pointer' }} onClick={() => navigate('/login')}>
          <div className="user-chip-left">
            <FiUser size={16} />
            {t('guest')}
          </div>
          <span className="ref-badge">{t('loginArrow')}</span>
        </div>
      )}
      {isLoggedIn === true && memberName && (
        <div
          className="user-chip"
          style={{ cursor: 'pointer' }}
          onClick={() => setContactExpanded(prev => !prev)}
        >
          <div className="user-chip-left">
            <FiUser size={16} />
            {memberName.toUpperCase()}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {memberId && <span className="ref-badge">{memberId}</span>}
            <FiChevronDown
              size={16}
              style={{ transform: contactExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}
            />
          </div>
        </div>
      )}

      {/* ── Kontaktinformasjon fields - members' are pre-filled from their
          account, so collapsed behind the chip by default; guests always see
          it expanded, nothing to collapse from. ── */}
      {(isLoggedIn !== true || contactExpanded) && (
        <>
          {isLoggedIn === null && <FormSkeleton />}

          {(isLoggedIn === true || isLoggedIn === false) && (
            <div className="cart-card">
              {/* Quick checkout for guests paying with Vipps: only the phone
                  number is typed here, name/email come back from Vipps's own
                  consent screen after payment. That consent isn't supported
                  for card payments, so guest+card still needs these fields. */}
              {(!guestMode || paymentMethod === 'CARD') && (
                <div className="field">
                  <label htmlFor="buyerName" className="field-label">{t('nameLabel')}</label>
                  <input id="buyerName" type="text" className="field-input" value={buyerName} onChange={e => setBuyerName(e.target.value)} placeholder={t('namePlaceholder')} required />
                </div>
              )}
              {(!guestMode || paymentMethod === 'CARD') && (
                <div className="field">
                  <label htmlFor="email" className="field-label">{t('emailLabel')}</label>
                  <input id="email" type="email" className="field-input" value={email} onChange={e => setEmail(e.target.value)} placeholder={t('emailPlaceholder')} required />
                </div>
              )}
              <div className="field">
                <label htmlFor="phone" className="field-label">{t('mobilLabel')}</label>
                <div className="phone-input-wrapper">
                  {/* WALLET must be a Norwegian Vipps account, so the +47 stays
                      fixed - CARD is the tourist/international path (Vipps's
                      own docs treat customer.phoneNumber there as optional,
                      unvalidated contact metadata), so it gets a real country
                      picker instead. A native <select> is deliberate here, not
                      a custom dropdown - iOS/Android both already render their
                      own native wheel/list picker for it for free. The select's
                      own text is made invisible and a compact flag+code span
                      sits behind it, so the closed box stays compact while the
                      open dropdown list (native, unaffected by this) still
                      shows the full flag+name+code per option. */}
                  {paymentMethod === 'CARD' ? (
                    <div className="phone-prefix-wrap">
                      <span className="phone-prefix phone-prefix-display" aria-hidden="true">
                        {(COUNTRY_CODES.find(c => c.code === countryCode) || COUNTRY_CODES[0]).flag} +{countryCode}
                      </span>
                      <select
                        className="phone-prefix-select"
                        value={countryCode}
                        onChange={e => setCountryCode(e.target.value)}
                        aria-label={t('countryCodeLabel')}
                      >
                        {COUNTRY_CODES.map(c => (
                          <option key={c.code} value={c.code}>{c.flag} {c.name} +{c.code}</option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    <span className="phone-prefix">🇳🇴 +47</span>
                  )}
                  <input
                    id="phone"
                    type="tel"
                    className="field-input phone-input"
                    value={phoneNumber}
                    onChange={handlePhoneChange}
                    placeholder={paymentMethod === 'CARD' ? t('mobilPlaceholderIntl') : "12345678"}
                    required
                    maxLength={paymentMethod === 'CARD' ? 12 : 8}
                  />
                </div>
              </div>
              {guestMode && paymentMethod === 'WALLET' && (
                <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#9AA8D0' }}>
                  {t('guestInfoFromVipps')}
                </p>
              )}
            </div>
          )}
        </>
      )}

      {/* ── Sammendrag section ── */}
      <div className="section-label">{t('sammendrag')}</div>

      <div className="cart-card">
        {cartItems.map((item, idx) => (
          <div key={item.id || item.productId || idx} className="summary-row">
            <div className="summary-row-left">
              <div className="qty-badge">{item.quantity}</div>
              <div className="summary-row-text">
                <span className="summary-row-name">{item.name}</span>
                {item.quantity > 1 && (
                  <span className="summary-row-unit-price">{item.price} kr/stk</span>
                )}
              </div>
            </div>
            <strong className="summary-row-price">{item.price * item.quantity} kr</strong>
          </div>
        ))}

        {/* ── Rabattkode - collapsed behind a text action until needed,
            instead of always showing an input row nobody uses yet ── */}
        <div className="discount-row">
          {discountStatus === 'valid' ? (
            <div className="discount-applied">
              <FiTag size={14} />
              <span className="discount-code-label">{discountCode}</span>
              <span className="discount-save">
                {discountData.discountType === 'percent'
                  ? `−${discountData.discountValue}%`
                  : `−${discountData.discountValue} kr`}
              </span>
              <button type="button" className="discount-remove" onClick={removeDiscount}>
                <FiXCircle size={15} />
              </button>
            </div>
          ) : discountFieldOpen ? (
            <div className="discount-input-row">
              <input
                className="field-input discount-field"
                type="text"
                placeholder={t('discountPlaceholder')}
                value={discountInput}
                onChange={e => { setDiscountInput(e.target.value.toUpperCase()); setDiscountError(''); }}
                onKeyDown={e => e.key === 'Enter' && applyDiscountCode()}
                maxLength={20}
                autoFocus
                style={{ letterSpacing: '0.06em', fontWeight: 700 }}
              />
              <button
                type="button"
                className={`discount-apply-btn${discountInput.trim() ? ' active-btn' : ''}`}
                onClick={applyDiscountCode}
                disabled={discountStatus === 'checking' || !discountInput.trim()}
              >
                {discountStatus === 'checking' ? '…' : t('discountApply')}
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="discount-toggle-link"
              onClick={() => setDiscountFieldOpen(true)}
            >
              {t('discountToggleLink')}
            </button>
          )}
          {discountError && <p className="discount-error">{discountError}</p>}
        </div>

        <div className="total-row">
          <span className="total-label">{t('totalsum')}</span>
          <span className="total-amount">{totalPrice} kr</span>
        </div>
        <div className="cart-divider" />
        <p className="mva-note">{t('mvaNote')}</p>
        {discountAmount > 0 && (
          <div className="discount-total-row">
            <FiCheckCircle size={13} />
            <span>{t('discountSavings', { amount: discountAmount, code: discountCode })}</span>
          </div>
        )}

        {isLoggedIn === null && <PointsSkeleton />}
        {isLoggedIn === true && (
          <div className="points-banner">
            <div className="points-banner-top">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
              </svg>
              {t('youEarnPoints', { points: pointsForPurchase(totalPrice, pointsPercent).toFixed(2) })}
            </div>
            {isPointsCampaign(pointsPercent) && <span className="points-campaign-badge">{t('pointsCampaign', { percent: pointsPercent })}</span>}
          </div>
        )}
      </div>

      {(isLoggedIn === true || isLoggedIn === false) && (
        <>
          {/* ── Betaling section ── */}
          <div className="section-label">{t('betaling')}</div>

          <div className="cart-card">
            {/* Freestanding cards (Vipps ePayment API's CARD paymentMethod) -
                mainly for tourists/international customers without the Vipps
                app. Same create/capture/refund flow either way. */}
            <div className="payment-method-toggle">
              <button
                type="button"
                className={`payment-method-btn${paymentMethod === 'WALLET' ? ' active' : ''}`}
                onClick={() => setPaymentMethod('WALLET')}
              >
                {t('vipps')}
              </button>
              <button
                type="button"
                className={`payment-method-btn${paymentMethod === 'CARD' ? ' active' : ''}`}
                onClick={() => setPaymentMethod('CARD')}
              >
                <FiCreditCard size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                {t('card')}
              </button>
            </div>

            {paymentMethod === 'WALLET' ? (
              // Official Vipps MobilePay button web component - loaded via the
              // script tag in public/index.html. Must not be replaced with a
              // custom-built button per Vipps' own design guidelines. language
              // follows the app's own i18n language, not hardcoded to Norwegian.
              <vipps-mobilepay-button
                brand="vipps"
                language={i18nInstance.language === 'en' ? 'en' : 'no'}
                variant="primary"
                rounded="true"
                verb="pay"
                stretched="true"
                compact="false"
                loading={loading ? "true" : "false"}
                onClick={handlePayNow}
              ></vipps-mobilepay-button>
            ) : (
              // Card payments have no Vipps-branding requirement - the
              // customer gets redirected to Vipps's own hosted card-entry
              // page regardless, so a plain button is fine here.
              <button type="button" className="btn-vipps" onClick={handlePayNow} disabled={loading}>
                {loading ? t('paying') : t('payWithCard', { price: totalPrice })}
              </button>
            )}
            <button type="button" className="btn-ghost" onClick={() => navigate('/Products')}>
              <FiArrowLeft size={15} style={{ marginRight: 6, verticalAlign: 'middle' }} />
              {t('backToProducts')}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
